import { Injectable, inject } from '@angular/core';
import { SafeResourceUrl } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { AuthenticationResult, EventMessage, EventType, PopupRequest, RedirectRequest } from '@azure/msal-browser';
import { Observable, catchError, filter, from, map, switchMap, tap, throwError } from 'rxjs';
import { User } from '../core/models/user.model';
import { GridLayoutService } from '../core/services/grid-layout.service';
import { HttpService } from '../core/services/http.service';
import { RuntimeConfigService } from '../core/services/runtime-config.service';
import { AuthService } from './auth.service';
import { AvatarService } from './avatar.service';
import { getProtectedResources } from './msal.config';

@Injectable()
export class EntraAuthService extends AuthService {
    private readonly msalService = inject(MsalService);
    private readonly msalBroadcastService = inject(MsalBroadcastService);
    private readonly router = inject(Router);
    private readonly avatarService = inject(AvatarService);
    private readonly httpService = inject(HttpService);
    private readonly runtimeConfigService = inject(RuntimeConfigService);
    private readonly gridLayoutService = inject(GridLayoutService);

    private scopes: string[] = [];

    constructor() {
        super();
        this.initializeAuth();
    }

    private initializeAuth(): void {
        this.scopes = ['openid', 'profile', 'email', ...getProtectedResources(this.runtimeConfigService.getConfig()).api.scopes];

        this.msalService.instance
            .initialize()
            .then(() => this.msalService.instance.handleRedirectPromise())
            .then((response: AuthenticationResult | null) => {
                if (response) {
                    this.handleAuthResponse(response);
                } else {
                    this.checkAuthenticationState();
                }

                this.msalBroadcastService.msalSubject$
                    .pipe(
                        filter((msg: EventMessage) => msg.eventType === EventType.LOGIN_SUCCESS),
                        map((msg: EventMessage) => msg.payload as AuthenticationResult)
                    )
                    .subscribe((result: AuthenticationResult) => {
                        this.handleAuthResponse(result);
                    });

                this.msalBroadcastService.msalSubject$
                    .pipe(filter((msg: EventMessage) => msg.eventType === EventType.LOGOUT_SUCCESS))
                    .subscribe(() => {
                        this.handleLogout();
                    });

                this.initializedSubject.next(true);
            })
            .catch(error => {
                console.error('Failed to initialize MSAL in AuthService:', error);
                this.initializedSubject.next(true);
            });
    }

    private handleAuthResponse(result: AuthenticationResult): void {
        if (result.account) {
            this.httpService.initHeaders();

            const user: User = {
                id: result.uniqueId || '',
                displayName: result.account.username || '',
                email: result.account.idTokenClaims?.['emails']?.[0] ?? '',
                familyName: (result.account.idTokenClaims?.['family_name'] as string) ?? '',
                firstName: (result.account.idTokenClaims?.['given_name'] as string) ?? '',
                roles: [],
                avatarUrl: (result.account.idTokenClaims?.['extension_Avatar'] as SafeResourceUrl) || null,
            };

            this.currentUserSubject.next(user);
            this.isAuthenticatedSubject.next(true);

            this.avatarService.loadUserAvatar(user, updatedUser => this.currentUserSubject.next(updatedUser));

            const redirectUrl = this.consumePostLoginRedirect();
            if (redirectUrl) {
                this.router.navigateByUrl(redirectUrl);
            } else {
                this.router.navigate(['/']);
            }
        }
    }

    private checkAuthenticationState(): void {
        const accounts = this.msalService.instance.getAllAccounts();
        if (accounts.length > 0) {
            const account = accounts[0];

            this.httpService.initHeaders();

            const user: User = {
                id: '',
                displayName: account.username || '',
                email: account.idTokenClaims?.['emails']?.[0] ?? '',
                familyName: (account.idTokenClaims?.['family_name'] as string) ?? '',
                firstName: (account.idTokenClaims?.['given_name'] as string) ?? '',
                roles: [],
                avatarUrl: (account.idTokenClaims?.['extension_Avatar'] as SafeResourceUrl) || null,
            };

            this.currentUserSubject.next(user);
            this.isAuthenticatedSubject.next(true);

            this.avatarService.loadUserAvatar(user, updatedUser => this.currentUserSubject.next(updatedUser));
        }
    }

    private handleLogout(): void {
        this.currentUserSubject.next(null);
        this.isAuthenticatedSubject.next(false);
        this.gridLayoutService.clearAllCaches();
    }

    public login(): void {
        const loginRequest: RedirectRequest = {
            scopes: ['openid', 'profile', 'email'],
        };

        this.msalService.instance
            .initialize()
            .then(() => {
                this.msalService.loginRedirect(loginRequest);
            })
            .catch(error => {
                console.error('Failed to initialize MSAL for login:', error);
            });
    }

    public loginPopup(): Observable<AuthenticationResult> {
        const loginRequest: PopupRequest = {
            scopes: ['openid', 'profile', 'email'],
        };

        return this.msalService.loginPopup(loginRequest);
    }

    public logout(): void {
        this.msalService.instance
            .initialize()
            .then(() => {
                this.msalService.logoutRedirect({
                    postLogoutRedirectUri: window.location.origin,
                });
            })
            .catch(error => {
                console.error('Failed to initialize MSAL for logout:', error);
            });
    }

    public logoutPopup(): Observable<void> {
        return this.msalService.logoutPopup({
            postLogoutRedirectUri: window.location.origin,
        });
    }

    public override isAuthenticated(): boolean {
        const accounts = this.msalService.instance.getAllAccounts();
        return accounts.length > 0 && this.isAuthenticatedSubject.value;
    }

    public getAccessToken(scopes: string[] = this.scopes): Observable<string> {
        return from(this.msalService.instance.initialize()).pipe(
            switchMap(() => {
                return this.msalService.acquireTokenSilent({
                    scopes,
                    account: this.msalService.instance.getAllAccounts()[0] || undefined,
                });
            }),
            map((response: AuthenticationResult) => response.accessToken),
            tap(() => {
                this.httpService.initHeaders();
            }),
            catchError(error => {
                console.warn('Silent token acquisition failed, attempting interactive login:', error);
                return this.handleTokenRefreshError(error, scopes);
            })
        );
    }

    private handleTokenRefreshError(error: any, scopes: string[]): Observable<string> {
        if (error.errorCode === 'interaction_required' || error.errorCode === 'consent_required') {
            return this.acquireTokenInteractive(scopes);
        }

        console.error('Token refresh failed, logging out user:', error);
        this.logout();
        return throwError(() => error);
    }

    private acquireTokenInteractive(scopes: string[]): Observable<string> {
        return from(
            this.msalService.acquireTokenPopup({
                scopes,
                account: this.msalService.instance.getAllAccounts()[0] || undefined,
            })
        ).pipe(
            map((response: AuthenticationResult) => response.accessToken),
            tap(() => {
                this.httpService.initHeaders();
            }),
            catchError(error => {
                console.error('Interactive token acquisition failed:', error);
                this.logout();
                return throwError(() => error);
            })
        );
    }

    public getAccessTokenWithRetry(scopes: string[] = this.scopes): Observable<string> {
        return this.getAccessToken(scopes).pipe(
            catchError(error => {
                if (error.errorCode === 'interaction_required' || error.errorCode === 'consent_required') {
                    console.warn('Silent token acquisition failed, attempting interactive login');
                    return this.acquireTokenInteractive(scopes);
                }

                console.error('Token acquisition failed, logging out user:', error);
                this.logout();
                return throwError(() => error);
            })
        );
    }

    public checkAndRefreshToken(): Observable<boolean> {
        if (!this.isAuthenticated()) {
            return throwError(() => new Error('User not authenticated'));
        }

        return this.getAccessToken().pipe(
            map(() => true),
            catchError(error => {
                console.error('Token refresh failed:', error);
                this.logout();
                return throwError(() => error);
            })
        );
    }
}
